import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useTheme } from '@mui/material/styles';
import { DesignIcon, SystemIcon } from '@monorepo/nova-icons';
import {
  ContentContainer,
  EmptyDescription,
  EmptyIconWrapper,
  EmptyStateContainer,
  HeaderRow,
  ModalHeader,
  ModalTitle,
  ModalWrapper,
  PaginationButton,
  PaginationContainer,
  PaginationNumberButton,
  PaginationNumberLabel,
  StyledDialog,
  TableBody,
  TableCell,
  TableHeaderCell,
  TableRow,
} from './profitable-days-modal.styled';

export interface ProfitableDayEntry {
  date: string;
}

interface ProfitableDaysModalProps {
  open: boolean;
  onClose: () => void;
  profitableDays: ProfitableDayEntry[];
}

const ITEMS_PER_PAGE = 5;

const formatDate = (isoDate: string) => dayjs(isoDate).format('DD MMM YYYY');

export const ProfitableDaysModal: React.FC<ProfitableDaysModalProps> = ({
  open,
  onClose,
  profitableDays,
}) => {
  const { t } = useTranslation('dashboardHeader');
  const [page, setPage] = useState(0);
  const theme = useTheme();
  useEffect(() => {
    if (!open) {
      setPage(0);
    }
  }, [open]);

  const sortedEntries = useMemo(
    () =>
      [...(profitableDays ?? [])].sort(
        (a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf(),
      ),
    [profitableDays],
  );

  const totalPages = Math.max(
    1,
    Math.ceil(sortedEntries.length / ITEMS_PER_PAGE),
  );

  useEffect(() => {
    setPage((previousPage) =>
      Math.min(previousPage, Math.max(totalPages - 1, 0)),
    );
  }, [totalPages]);

  const paginatedEntries = useMemo(() => {
    const start = page * ITEMS_PER_PAGE;
    return sortedEntries.slice(start, start + ITEMS_PER_PAGE);
  }, [page, sortedEntries]);

  const showPagination = totalPages > 1;
  const isEmpty = sortedEntries.length === 0;

  const handlePrevPage = () => {
    setPage((previousPage) => Math.max(previousPage - 1, 0));
  };

  const handleNextPage = () => {
    setPage((previousPage) => Math.min(previousPage + 1, totalPages - 1));
  };

  const handleSelectPage = (nextPage: number) => {
    setPage(nextPage);
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="profitable-days-modal-title"
    >
      <ModalWrapper>
        <ModalHeader id="profitable-days-modal-title">
          <ModalTitle variant="h3">
            {t('objectives.profitableDays.modalTitle')}
          </ModalTitle>
        </ModalHeader>

        {isEmpty ? (
          <EmptyStateContainer>
            <EmptyIconWrapper>
              <DesignIcon
                iconName={
                  theme.palette.mode === 'dark'
                    ? 'EmptyStateSmilleyDark'
                    : 'EmptyStateSmilley'
                }
                useMuiSvgIcon={false}
              />
            </EmptyIconWrapper>
            <EmptyDescription variant="subtitle2">
              {t('objectives.profitableDays.emptyState')}
            </EmptyDescription>
          </EmptyStateContainer>
        ) : (
          <>
            <ContentContainer>
              <HeaderRow>
                <TableHeaderCell variant="subtitle2">
                  {t('objectives.profitableDays.dateColumn')}
                </TableHeaderCell>
              </HeaderRow>
              <TableBody>
                {paginatedEntries.map((entry) => (
                  <TableRow key={entry.date}>
                    <TableCell>{formatDate(entry.date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ContentContainer>

            {showPagination && (
              <PaginationContainer>
                <PaginationButton
                  onClick={handlePrevPage}
                  disabled={page === 0}
                >
                  <SystemIcon iconName="CaretLeft" size={14} />
                </PaginationButton>
                {Array.from({ length: totalPages }).map((_, index) => (
                  <PaginationNumberButton
                    key={`profitable-days-page-${index}`}
                    active={index === page}
                    size="sm"
                    onClick={() => handleSelectPage(index)}
                  >
                    <PaginationNumberLabel>{index + 1}</PaginationNumberLabel>
                  </PaginationNumberButton>
                ))}
                <PaginationButton
                  onClick={handleNextPage}
                  disabled={page === totalPages - 1}
                >
                  <SystemIcon iconName="CaretRight" size={14} />
                </PaginationButton>
              </PaginationContainer>
            )}
          </>
        )}
      </ModalWrapper>
    </StyledDialog>
  );
};
